import { useEffect, useState } from "react";
import { approveAgentSetup, getAgentSetup, type AgentSetupDetails } from "../../lib/agent-client";
import { posthog } from "../../lib/posthog";

type Visibility = "public" | "password" | "workspace_private";

export function AgentSetupPage({ operationId, approvalState }: { operationId: string; approvalState?: string }) {
  const [details, setDetails] = useState<AgentSetupDetails | null>(null);
  const [visibility, setVisibility] = useState<Visibility>("public");
  const [password, setPassword] = useState("");
  const [projectId, setProjectId] = useState("");
  const [status, setStatus] = useState<"loading" | "ready" | "saving" | "approved" | "failed">("loading");
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const setup = approvalState ? getAgentSetup(operationId, approvalState) : getAgentSetup(operationId);
    void setup.then((value) => {
      if (!active) return;
      setDetails(value);
      setProjectId(value.projectId ?? "");
      setStatus("ready");
    }).catch((error: unknown) => {
      if (!active) return;
      setMessage(error instanceof Error ? error.message : "Doorway could not open this setup request.");
      setStatus("failed");
    });
    return () => { active = false; };
  }, [approvalState, operationId]);

  if (status === "loading") {
    return <section className="flow-card agent-setup-card"><span className="progress-orbit" aria-label="Loading agent setup" /></section>;
  }
  if (status === "failed" || !details) {
    return <section className="flow-card agent-setup-card"><h1>We could not open this request</h1><p className="error-message" role="alert">{message}</p></section>;
  }
  if (status === "approved") {
    return (
      <section className="flow-card agent-setup-card" aria-labelledby="agent-approved-heading">
        <div className="agent-setup-success" aria-hidden="true">✓</div>
        <p className="step-label">Approved</p>
        <h1 id="agent-approved-heading">Doorway can publish this app.</h1>
        <p>{visibility === "password"
          ? "Return to Claude or Codex. Publishing will continue automatically, and your password stays in Doorway."
          : visibility === "workspace_private"
            ? "Return to Claude or Codex. Publishing will continue automatically for your workspace."
            : "Return to Claude or Codex. Publishing will continue automatically with public access."}</p>
      </section>
    );
  }

  const passwordInvalid = visibility === "password" && (password.length < 8 || password.length > 256);
  const projectChoiceAvailable = Boolean(details.projects && details.projects.length > 1);
  const projectSelectionRequired = projectChoiceAvailable && !projectId;
  const selectedProject = details.projects?.find((project) => project.projectId === projectId);
  return (
    <section className="flow-card agent-setup-card" aria-labelledby="agent-setup-heading">
      <p className="step-label">Agent publishing</p>
      <h1 id="agent-setup-heading">
        {projectChoiceAvailable ? "Choose where this app lives and who can open it." : "Choose who can open this app."}
      </h1>
      <div className="agent-reviewed-files">
        <span className="agent-reviewed-check" aria-hidden="true">✓</span>
        <span>
          <strong>Files reviewed and ready</strong>
          <small>Doorway will publish only the files prepared in this request.</small>
        </span>
      </div>

      {details.projects && details.projects.length > 1 ? (
        <fieldset className="agent-project-options">
          <legend>Publish to</legend>
          <p>Choose the Doorway project that should receive this version.</p>
          <div>
            {details.projects.map((project) => (
              <label className={projectId === project.projectId ? "agent-project-option selected" : "agent-project-option"} key={project.projectId}>
                <input
                  type="radio"
                  name="project"
                  value={project.projectId}
                  checked={projectId === project.projectId}
                  onChange={() => setProjectId(project.projectId)}
                />
                <span>
                  <strong>{project.projectName}</strong>
                  <small>{project.workspaceName}</small>
                </span>
                <i aria-hidden="true">✓</i>
              </label>
            ))}
          </div>
          <small>Only projects you can publish are shown.</small>
        </fieldset>
      ) : selectedProject ? (
        <div className="agent-project-summary">
          <span>Publishing to</span>
          <strong>{selectedProject.projectName}</strong>
          <small>{selectedProject.workspaceName}</small>
        </div>
      ) : null}

      <fieldset className="visibility-options">
        <legend>App access</legend>
        <label className={visibility === "public" ? "visibility-option selected" : "visibility-option"}>
          <input type="radio" name="visibility" value="public" checked={visibility === "public"} onChange={() => setVisibility("public")} />
          <span><strong>Public</strong><small>Anyone with the link can open it.</small></span>
        </label>
        <label className={visibility === "password" ? "visibility-option selected" : "visibility-option"}>
          <input type="radio" name="visibility" value="password" checked={visibility === "password"} onChange={() => setVisibility("password")} />
          <span><strong>Password protected</strong><small>Visitors must enter the password you choose.</small></span>
        </label>
        {approvalState && (
          <label className={visibility === "workspace_private" ? "visibility-option selected" : "visibility-option"}>
            <input type="radio" name="visibility" value="workspace_private" checked={visibility === "workspace_private"} onChange={() => setVisibility("workspace_private")} />
            <span><strong>Workspace only</strong><small>Only signed-in members of this workspace can open it.</small></span>
          </label>
        )}
      </fieldset>

      {visibility === "password" && (
        <label className="agent-password-field">
          Visitor password
          <input type="password" value={password} minLength={8} maxLength={256} autoComplete="new-password" onChange={(event) => setPassword(event.target.value)} />
          <small>This password is sent directly to Doorway and is never shared with the agent.</small>
        </label>
      )}

      {message && <p className="error-message" role="alert">{message}</p>}
      <button
        className="primary-button"
        type="button"
        disabled={status === "saving" || passwordInvalid || projectSelectionRequired}
        onClick={() => {
          setStatus("saving");
          setMessage(null);
          const selectedProject = details.projects && projectId ? { projectId } : {};
          const choice = visibility === "password"
            ? { visibility, password, ...selectedProject } as const
            : { visibility, ...selectedProject } as const;
          const approval = approvalState
            ? approveAgentSetup(operationId, choice, approvalState)
            : approveAgentSetup(operationId, choice);
          void approval.then(() => {
            posthog.capture("agent_setup_approved", { visibility });
            setStatus("approved");
          }).catch((error: unknown) => {
            setMessage(error instanceof Error ? error.message : "Doorway could not approve this request.");
            setStatus("ready");
          });
        }}
      >
        {status === "saving" ? "Approving..." : "Approve and publish"}
      </button>
      <p className="agent-setup-note">Provider keys and model choices stay in Doorway project settings and never enter the agent conversation.</p>
    </section>
  );
}
